import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams, useLocation as useRouterLocation } from "react-router-dom";
import { AlertCircle, ArrowUp, Check, FileSpreadsheet, LayoutGrid, List, Plus, RefreshCw, Save, Search, Trash2, Upload } from "lucide-react";
import RoleGuard from "@/components/auth/RoleGuard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ConvertedCostIndicator } from "@/components/cost/ConvertedCostIndicator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/toaster";
import { PageErrorBoundary } from "@/components/fallbacks";
import { PageLoader } from "@/components/loading/PageLoader";
import PurchaseOrderCsvUploadModal from "@/features/purchase-orders/components/PurchaseOrderCsvUploadModal";
import ProductImageWithHover from "@/features/purchase-orders/components/ProductImageWithHover";
import { useSubmitOrder } from "@/features/purchase-orders";
import { trpc } from "@/lib/trpc";
import { usePurchaseableSkus } from "@/features/inventory/hooks/usePurchaseableSkus";
import { useLocations } from "@/features/locations/hooks/useLocations";
import { useSuppliers } from "@/features/suppliers/hooks/useSuppliers";
import QuickAddSupplierModal from "@/features/suppliers/components/QuickAddSupplierModal";
import { useCurrencyFormatter } from "@/hooks/useCurrencyFormatter";
import { toast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { reportError } from "@/lib/logging";
import { sizeRank } from "@/lib/variant-sort";
import type { Product } from "@/features/inventory/types";
import { useLocation } from "@/contexts/LocationContext";
import { LocationSelectionPrompt } from "@/components/layout/LocationSelectionPrompt";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { BackToTopPill } from "@/components/BackToTopPill";
import { getScrollContainer } from "@/lib/scroll-container";
import { cn } from "@/lib/utils";

type LineItem = {
  skuId: string;
  quantity: number;
};

type LineItemRow = {
  lineItem: LineItem;
  product: Product;
  unitCost: number | null;
  lineTotal: number | null;
};

type CsvImportItem = {
  productId: string;
  sku: string;
  quantity: number;
  rowCount: number;
  /** Populated when the SKU isn't stocked at the current location yet. */
  newProduct?: Product;
};

const SUPPLIER_EMPTY_VALUE = "__none__";
const SUPPLIER_ADD_NEW_VALUE = "__add_new_supplier__";

const colorHexMap: Record<string, string> = {
  'black': '#000000', 'natural': '#EBE9DF', 'powder blue': '#0EA5E9',
  'charcoal': '#36454F', 'lilac': '#9370DB', 'pale blue': '#B0E0E6',
  'black ringer': '#000000', 'red ringer': '#FF0000', 'white': '#FFFFFF',
  'red': '#FF0000', 'blue': '#0000FF', 'green': '#008000', 'yellow': '#FFFF00',
  'purple': '#800080', 'pink': '#FFC0CB', 'orange': '#FFA500', 'brown': '#8B4513',
  'black stone': '#414040', 'grey stone': '#535252', 'grey': '#808080',
  'gray': '#808080', 'navy': '#000080', 'beige': '#F5F5DC', 'cream': '#FFFDD0',
  'maroon': '#800000', 'teal': '#008080', 'olive': '#808000', 'silver': '#C0C0C0',
  'gold': '#FFD700',
};

const renderColorCircle = (color: string) => {
  const isRinger = color.toLowerCase().includes('ringer');
  if (isRinger) {
    const ringerColor = color.toLowerCase().includes('black') ? '#000000' : '#FF0000';
    return (
      <div
        className="w-4 h-4 rounded-full bg-white"
        style={{ border: `2px solid ${ringerColor}`, boxSizing: 'border-box' }}
      />
    );
  }
  return (
    <div
      className="w-4 h-4 rounded-full border border-border"
      style={{ backgroundColor: colorHexMap[color.toLowerCase()] || '#CCCCCC' }}
    />
  );
};

const getProductDescription = (product: Product) => {
  const parts = [product.title, product.category.type, product.category.color, product.category.size].filter(Boolean);
  return parts.join(" · ") || product.sku;
};

const OrderCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routerLocation = useRouterLocation();
  // Prefill from any "Create PO from selection" flow (router state). Two shapes
  // are accepted so callers can migrate at their own pace:
  //   - preselectedSkuIds: string[]                (quantity 1 each — legacy)
  //   - preselectedItems: { skuId, quantity }[]    (quantities honoured, min 1)
  // Read once at mount so the picker starts preselected. When a skuId appears in
  // both, the explicit item quantity wins. Unknown skuIds are dropped downstream
  // (lineItemRows skips rows with no matching product).
  const routerState = routerLocation.state as {
    preselectedSkuIds?: string[];
    preselectedItems?: { skuId: string; quantity: number }[];
  } | null;
  const preselectedSkuIds = routerState?.preselectedSkuIds;
  const preselectedItems = routerState?.preselectedItems;
  // `/orders/new?type=transfer` is a legacy link/bookmark shape — transfers
  // now have their own create page (see the redirect below).
  const isLegacyTransferLink = searchParams.get('type') === 'transfer';

  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const { products, loading: inventoryLoading } = usePurchaseableSkus(selectedLocation?.id);
  const {
    createOrder,
    createDraftOrder,
    isCreating,
    isCreatingDraft,
  } = useSubmitOrder();
  const { locations, isLoading: locationsLoading } = useLocations();
  const { suppliers, isLoading: suppliersLoading } = useSuppliers({ includeInactive: false });
  const { format: formatCurrency } = useCurrencyFormatter();

  const [lineItems, setLineItems] = useState<LineItem[]>(() => {
    const byId = new Map<string, number>();
    if (Array.isArray(preselectedSkuIds)) {
      for (const skuId of preselectedSkuIds) {
        if (typeof skuId === 'string' && skuId) byId.set(skuId, 1);
      }
    }
    if (Array.isArray(preselectedItems)) {
      for (const item of preselectedItems) {
        if (item && typeof item.skuId === 'string' && item.skuId) {
          const qty = Math.max(1, Math.floor(Number(item.quantity) || 1));
          byId.set(item.skuId, qty);
        }
      }
    }
    return Array.from(byId, ([skuId, quantity]) => ({ skuId, quantity }));
  });
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  // BLU-570: guard the bulk "clear all products" action behind a confirm.
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);

  // Currency & exchange rate
  const [currency, setCurrency] = useState("AUD");
  const [exchangeRate, setExchangeRate] = useState("1");
  const isNonAud = currency !== "AUD";
  const parsedRate = Number(exchangeRate) || 1;

  // SKU search + multi-select state
  const [skuSearch, setSkuSearch] = useState("");
  const [skuDropdownOpen, setSkuDropdownOpen] = useState(false);
  const [checkedSkuIds, setCheckedSkuIds] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedSupplier = useMemo(
    () => suppliers.find((supplier) => supplier.id === supplierId) ?? null,
    [supplierId, suppliers]
  );
  const supplierName = selectedSupplier?.title ?? "";

  // BLU-373 PR2b: track previous currency so the toast only fires on actual switches.
  // Synced to the live `currency` state so manual dropdown edits also update the
  // baseline — without this, picking supplier-A (USD) → editing dropdown to EUR
  // → picking supplier-B (USD) would silently no-op (ref still says USD).
  const prevCurrencyRef = useRef<string>(currency);
  useEffect(() => {
    prevCurrencyRef.current = currency;
  }, [currency]);

  // Resolve the live org base currency for the toast description (no jargon)
  const orgBaseCurrency = (organization?.default_currency ?? 'AUD').toUpperCase();

  // Auto-set currency from supplier's default_currency when supplier changes.
  // BLU-373 PR2b: surface the auto-switch with a visible toast so the user can
  // never miss it (silent FX switches were half of how BLU-373 went unnoticed).
  useEffect(() => {
    if (!selectedSupplier) return;
    const supplierCurrency = (selectedSupplier as { default_currency?: string | null }).default_currency;
    if (!supplierCurrency || supplierCurrency.trim().length !== 3) return;

    const newCurrency = supplierCurrency.trim().toUpperCase();
    if (newCurrency === prevCurrencyRef.current) return;

    setCurrency(newCurrency);
    // prevCurrencyRef will be updated by the [currency] effect above on re-render
    toast({
      title: `Currency set to ${newCurrency}`,
      description: `${selectedSupplier.title ?? 'Supplier'} invoices in ${newCurrency}. The PO will be authored in ${newCurrency} and converted to ${orgBaseCurrency} on receive.`,
    });
  }, [selectedSupplier, orgBaseCurrency]);

  // Fetch live exchange rate when currency is non-AUD
  const exchangeRateQuery = trpc.purchaseOrders.getExchangeRate.useQuery(
    { baseCurrency: currency, targetCurrency: "AUD" },
    { enabled: isNonAud, staleTime: 5 * 60_000 }
  );

  // Auto-populate exchange rate when data arrives
  useEffect(() => {
    if (exchangeRateQuery.data && isNonAud) {
      setExchangeRate(String(exchangeRateQuery.data.rate));
    }
  }, [exchangeRateQuery.data, isNonAud]);

  // Reset rate when switching back to AUD
  useEffect(() => {
    if (!isNonAud) {
      setExchangeRate("1");
    }
  }, [isNonAud]);

  // SKUs imported via CSV that aren't yet stocked at this location.
  // Backend auto-activates on order save; we merge them locally so rows render.
  const [csvExtraProducts, setCsvExtraProducts] = useState<Product[]>([]);
  const productMap = useMemo(() => new Map(products.map((product) => [product.id, product])), [products]);
  const csvExtraProductMap = useMemo(
    () => new Map(csvExtraProducts.map((product) => [product.id, product])),
    [csvExtraProducts]
  );

  const lineItemRows = useMemo<LineItemRow[]>(() => {
    const rate = isNonAud && parsedRate > 0 ? parsedRate : 1;
    const rows = lineItems.reduce<LineItemRow[]>((acc, lineItem) => {
      const product =
        productMap.get(lineItem.skuId) ??
        csvExtraProductMap.get(lineItem.skuId);
      if (!product) return acc;
      const audCost = product.effectiveCost ?? null;
      const unitCost = audCost === null ? null : (isNonAud ? Math.round((audCost / rate + Number.EPSILON) * 100) / 100 : audCost);
      const lineTotal = unitCost === null ? null : Math.round((unitCost * lineItem.quantity + Number.EPSILON) * 100) / 100;
      acc.push({ lineItem, product, unitCost, lineTotal });
      return acc;
    }, []);
    rows.sort((a, b) => a.product.sku.localeCompare(b.product.sku));
    return rows;
  }, [lineItems, productMap, csvExtraProductMap, isNonAud, parsedRate]);

  const totals = useMemo(() => {
    const totalQty = lineItemRows.reduce((sum, row) => sum + row.lineItem.quantity, 0);
    const missingCostCount = lineItemRows.filter((row) => row.unitCost === null).length;
    const totalCost =
      missingCostCount > 0
        ? null
        : lineItemRows.reduce((sum, row) => sum + (row.lineTotal ?? 0), 0);
    return { totalQty, totalCost, missingCostCount };
  }, [lineItemRows]);

  const orderQuantities = useMemo(() => {
    return lineItems.reduce<Record<string, number>>((acc, lineItem) => {
      if (lineItem.quantity > 0) {
        acc[lineItem.skuId] = lineItem.quantity;
      }
      return acc;
    }, {});
  }, [lineItems]);

  const selectedSkuIds = useMemo(() => new Set(lineItems.map((item) => item.skuId)), [lineItems]);

  // Stepper gate: Order Details must be valid before Line Items unlocks.
  // Title + Supplier are the minimum.
  const orderDetailsValid = title.trim().length > 0 && supplierName.trim().length > 0;

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (title.trim().length === 0) missing.push("Title");
    if (supplierName.trim().length === 0) missing.push("Supplier");
    return missing;
  }, [title, supplierName]);

  // One-shot brand-purple glow on Line Items the moment Order Details
  // first becomes valid. The CSS keyframe is wrapped in `motion-safe:` so
  // the visual animation is suppressed under prefers-reduced-motion; we
  // ALSO short-circuit the setTimeout per DESIGN.md §5's hard requirement
  // ("any setTimeout-driven effect must check matchMedia and short-circuit").
  // Note: timeout is 1200ms but the keyframe runs 1100ms — the 100ms buffer
  // ensures the animation completes before the class is stripped.
  const [playUnlockGlow, setPlayUnlockGlow] = useState(false);
  const wasValidRef = useRef(orderDetailsValid);
  useEffect(() => {
    if (!wasValidRef.current && orderDetailsValid) {
      const reduced =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      wasValidRef.current = orderDetailsValid;
      if (reduced) return undefined;
      setPlayUnlockGlow(true);
      const id = window.setTimeout(() => setPlayUnlockGlow(false), 1200);
      return () => window.clearTimeout(id);
    }
    wasValidRef.current = orderDetailsValid;
    return undefined;
  }, [orderDetailsValid]);

  // Page chrome: centred title + typewriter back button via TopHeader.
  // No subtitle on create — the form itself is the subject.
  usePageTitle("New Purchase Order", null, { label: "Back to Orders", to: "/orders" });

  // Always start at the top — the actual scroll surface is SidebarInset,
  // so window.scrollTo would be a silent no-op (DESIGN.md §6).
  useEffect(() => {
    getScrollContainer(null)?.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // Matrix view data: group line items by product type → color (rows) x size (columns)
  const matrixData = useMemo(() => {
    const grouped: Record<string, {
      colors: string[];
      sizes: string[];
      matrix: Record<string, Record<string, {
        skuId: string; quantity: number; stockLevel: number; imageUrl?: string;
      }>>;
    }> = {};

    for (const row of lineItemRows) {
      const type = row.product.category.type || 'Unknown';
      const color = row.product.category.color || 'Unknown';
      const size = row.product.category.size || 'Unknown';

      if (!grouped[type]) {
        grouped[type] = { colors: [], sizes: [], matrix: {} };
      }
      const group = grouped[type];
      if (!group.colors.includes(color)) group.colors.push(color);
      if (!group.sizes.includes(size)) group.sizes.push(size);
      if (!group.matrix[color]) group.matrix[color] = {};
      group.matrix[color][size] = {
        skuId: row.product.id,
        quantity: row.lineItem.quantity,
        stockLevel: row.product.stockLevel,
        imageUrl: row.product.image_url,
      };
    }

    for (const group of Object.values(grouped)) {
      group.colors.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      group.sizes.sort((a, b) => sizeRank(a) - sizeRank(b));
    }

    return grouped;
  }, [lineItemRows]);

  // Multi-word search: each term must match somewhere across all fields (same as inventory page)
  const MAX_VISIBLE_RESULTS = 50;
  const { availableResults, addedResults } = useMemo(() => {
    const terms = skuSearch.trim().split(/\s+/).filter((t) => t.length > 0);
    if (terms.length === 0) return { availableResults: [] as Product[], addedResults: [] as Product[] };
    const lowerTerms = terms.map((t) => t.toLowerCase());
    const available: Product[] = [];
    const added: Product[] = [];
    for (const product of products) {
      const haystack = [
        product.sku,
        product.title || "",
        product.category.type || "",
        product.category.color || "",
        product.category.size || "",
      ].join(" ").toLowerCase();
      if (!lowerTerms.every((term) => haystack.includes(term))) continue;
      if (selectedSkuIds.has(product.id)) {
        added.push(product);
      } else {
        available.push(product);
      }
      if (available.length >= MAX_VISIBLE_RESULTS && added.length >= MAX_VISIBLE_RESULTS) break;
    }
    // Sort by color (grouped), then size (XS→4XL)
    const sortProducts = (arr: Product[]) =>
      [...arr].sort((a, b) => {
        const colorA = (a.category.color ?? '').toLowerCase();
        const colorB = (b.category.color ?? '').toLowerCase();
        if (colorA !== colorB) return colorA.localeCompare(colorB);
        return sizeRank(a.category.size) - sizeRank(b.category.size);
      });
    return {
      availableResults: sortProducts(available.slice(0, MAX_VISIBLE_RESULTS)),
      addedResults: sortProducts(added.slice(0, MAX_VISIBLE_RESULTS)),
    };
  }, [products, skuSearch, selectedSkuIds]);

  const canSubmit =
    title.trim().length > 0 &&
    supplierName.trim().length > 0 &&
    Object.keys(orderQuantities).length > 0 &&
    !isCreating &&
    !isCreatingDraft;

  const resetForm = () => {
    setLineItems([]);
    setTitle("");
    setDescription("");
    setSupplierId("");
    setSkuSearch("");
    setCheckedSkuIds(new Set());
    setSkuDropdownOpen(false);
    setCurrency("AUD");
    setExchangeRate("1");
  };

  const upsertLineItem = (skuId: string, quantity: number, mode: "set" | "increment" = "increment") => {
    if (quantity <= 0) return;
    setLineItems((prev) => {
      const existing = prev.find((item) => item.skuId === skuId);
      if (!existing) {
        return [...prev, { skuId, quantity }];
      }
      return prev.map((item) =>
        item.skuId === skuId
          ? {
              ...item,
              quantity: mode === "set" ? quantity : item.quantity + quantity,
            }
          : item
      );
    });
  };

  const toggleCheckedSku = useCallback((skuId: string) => {
    setCheckedSkuIds((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) {
        next.delete(skuId);
      } else {
        next.add(skuId);
      }
      return next;
    });
  }, []);

  const addCheckedSkus = useCallback(() => {
    checkedSkuIds.forEach((skuId) => {
      upsertLineItem(skuId, 1, "set");
    });
    setCheckedSkuIds(new Set());
    setSkuSearch("");
    setSkuDropdownOpen(false);
  }, [checkedSkuIds]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setSkuDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && skuDropdownOpen) {
        setSkuDropdownOpen(false);
        searchInputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [skuDropdownOpen]);

  const updateLineItemQuantity = (skuId: string, value: string) => {
    if (value === "") {
      setLineItems((prev) =>
        prev.map((item) => (item.skuId === skuId ? { ...item, quantity: 0 } : item))
      );
      return;
    }
    if (!/^\d+$/.test(value)) return;
    const quantity = Number.parseInt(value, 10);
    setLineItems((prev) =>
      prev.map((item) => (item.skuId === skuId ? { ...item, quantity } : item))
    );
  };

  const removeLineItem = (skuId: string) => {
    setLineItems((prev) => prev.filter((item) => item.skuId !== skuId));
  };

  const handleCsvApply = (items: CsvImportItem[]) => {
    setLineItems((prev) => {
      const next = [...prev];
      items.forEach((item) => {
        const existingIndex = next.findIndex((line) => line.skuId === item.productId);
        if (existingIndex >= 0) {
          const existing = next[existingIndex];
          if (existing) {
            next[existingIndex] = { ...existing, quantity: item.quantity };
          }
        } else {
          next.push({ skuId: item.productId, quantity: item.quantity });
        }
      });
      return next;
    });
    // Merge any "new at location" SKUs so they render before order save.
    const newProducts = items
      .map((i) => i.newProduct)
      .filter((p): p is Product => Boolean(p));
    if (newProducts.length > 0) {
      setCsvExtraProducts((prev) => {
        const knownIds = new Set([
          ...products.map((p) => p.id),
          ...prev.map((p) => p.id),
        ]);
        const toAdd = newProducts.filter((p) => !knownIds.has(p.id));
        return toAdd.length === 0 ? prev : [...prev, ...toAdd];
      });
    }
  };

  const submitOrder = async (mode: "draft" | "submitted") => {
    if (!canSubmit) {
      toast({
        title: "Missing required fields",
        description: "Provide order title, supplier, and at least one line item.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Merge CSV-added new-at-location SKUs into products so the submit
      // hook's itemsToOrder filter includes them. Cast to bridge the
      // useInventory Product type vs the typed Product used by csvExtras.
      const knownProductIds = new Set(products.map((p) => p.id));
      const extrasNotYetInProducts = csvExtraProducts.filter((p) => !knownProductIds.has(p.id));
      const productsForPayload =
        extrasNotYetInProducts.length === 0
          ? products
          : [...products, ...(extrasNotYetInProducts as unknown as typeof products)];

      const payload = {
        products: productsForPayload,
        supplierId: supplierId || null,
        supplierName,
        orderQuantities,
        title: title.trim(),
        description: description.trim() || undefined,
        ...(isNonAud ? { currency, exchangeRate: parsedRate } : {}),
        orderType: 'purchase' as const,
      };

      if (mode === "submitted") {
        await createOrder(payload);
      } else {
        await createDraftOrder(payload);
      }

      resetForm();
      navigate("/orders");
    } catch (error) {
      reportError("Error creating purchase order", error);
    }
  };

  // Transfers now have their own create page — redirect legacy
  // `/orders/new?type=transfer` links/bookmarks so this page stays PO-only.
  if (isLegacyTransferLink) {
    return <Navigate to="/transfers/new" replace />;
  }

  if (inventoryLoading || locationsLoading) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col">
          <PageLoader pool="purchaseOrders" />
        </div>
      </RoleGuard>
    );
  }

  if (!inventoryLoading && products.length === 0) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col items-center justify-center p-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 max-w-md">
            <h2 className="text-xl font-semibold text-amber-700 mb-2">
              No Inventory Available
            </h2>
            <p className="text-amber-700">
              Add inventory items before creating purchase orders.
            </p>
          </div>
        </div>
      </RoleGuard>
    );
  }

  if (!locationsLoading && locations.length > 0 && !selectedLocation) {
    return (
      <LocationSelectionPrompt
        locations={locations}
        title="Select a Location"
        description="Select the destination location for this purchase order."
      />
    );
  }

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6">
          {/* Sticky action bar — first child per DESIGN.md §3.1 so position:sticky
              actually sticks. z-40 per DESIGN.md §2 (page action bar). Totals on
              the left, primary actions on the right. Layout + button styling
              mirrors the edit-screen sticky bar. Bar always renders so the
              zero-state and the in-progress state share the same chrome — the
              "always-render" exception for create-flow is documented in
              DESIGN.md §3.5. */}
          <div
            className="sticky z-40 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 border-b bg-background/85 supports-[backdrop-filter]:bg-background/65 backdrop-blur"
            style={{ top: "var(--top-header-height, 3.5rem)" }}
          >
            <div className="mx-auto flex max-w-screen-2xl flex-wrap items-end justify-between gap-x-4 gap-y-2 pt-3 pb-2.5">
              <div className="flex flex-wrap items-end gap-2">
                <Badge variant="secondary" className="h-8 px-3 text-[11px] tabular-nums">{lineItemRows.length} Products</Badge>
                <Badge variant="secondary" className="h-8 px-3 text-[11px] tabular-nums">{totals.totalQty.toLocaleString()} Items</Badge>
                <Badge variant="secondary" className="h-8 px-3 text-[11px] tabular-nums">
                  {totals.totalCost === null ? "-" : formatCurrency(totals.totalCost, currency)}
                </Badge>
              </div>
              <div className="flex flex-wrap items-end gap-1.5">
                <Button
                  variant="outline"
                  type="button"
                  size="sm"
                  onClick={() => setShowCsvImportModal(true)}
                  disabled={!orderDetailsValid}
                  title={!orderDetailsValid ? "Fill out the required Order Details first." : undefined}
                  className="h-8 px-3 text-xs gap-1.5"
                >
                  <Upload className="h-3.5 w-3.5" />
                  Upload CSV
                </Button>
                <span aria-hidden className="mx-1 h-5 w-px self-end bg-border/60" />
                <Button
                  variant="outline"
                  type="button"
                  size="sm"
                  disabled={!canSubmit}
                  onClick={() => void submitOrder("draft")}
                  className="h-8 px-3 text-xs gap-1.5"
                >
                  <Save className="h-3.5 w-3.5" />
                  {isCreatingDraft ? "Saving…" : "Save Draft"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={!canSubmit}
                  onClick={() => void submitOrder("submitted")}
                  className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {isCreating ? "Submitting…" : "Submit Order"}
                </Button>
              </div>
            </div>
          </div>

          {/* STEP 1 — Order Details (always interactive) + Summary side-by-side. */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Order Title<span className="text-destructive ml-1">*</span></Label>
                    <Input
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      placeholder="e.g. Winter restock"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Supplier<span className="text-destructive ml-1">*</span></Label>
                    <Select
                      value={supplierId || SUPPLIER_EMPTY_VALUE}
                      onValueChange={(value) => {
                        if (value === SUPPLIER_ADD_NEW_VALUE) {
                          setIsSupplierModalOpen(true);
                          return;
                        }
                        setSupplierId(value === SUPPLIER_EMPTY_VALUE ? "" : value);
                      }}
                      disabled={suppliersLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={suppliersLoading ? "Loading..." : "Select supplier"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SUPPLIER_ADD_NEW_VALUE}>+ Add new supplier</SelectItem>
                        <SelectItem value={SUPPLIER_EMPTY_VALUE}>Select supplier…</SelectItem>
                        {suppliers.length === 0 ? (
                          <SelectItem value="__no_suppliers__" disabled>
                            No suppliers available
                          </SelectItem>
                        ) : (
                          suppliers.map((supplier) => (
                            <SelectItem key={supplier.id} value={supplier.id}>
                              {supplier.title}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder="Optional notes for this purchase order"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["AUD", "USD", "CNY", "EUR", "GBP"].map((code) => (
                          <SelectItem key={code} value={code}>{code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Exchange Rate</Label>
                    <div className="flex gap-2">
                      <Input
                        value={exchangeRate}
                        onChange={(e) => setExchangeRate(e.target.value)}
                        disabled={!isNonAud}
                        placeholder="1.0000"
                        inputMode="decimal"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        disabled={!isNonAud || exchangeRateQuery.isFetching}
                        onClick={() => void exchangeRateQuery.refetch()}
                        title="Refresh exchange rate"
                      >
                        <RefreshCw className={`h-4 w-4 ${exchangeRateQuery.isFetching ? "animate-spin" : ""}`} />
                      </Button>
                    </div>
                    {isNonAud && parsedRate > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        1 {currency} = {parsedRate.toFixed(4)} AUD
                      </p>
                    ) : null}
                  </div>
                </div>

                {selectedLocation ? (
                  <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                    Location: <span className="font-medium text-foreground">{selectedLocation.name}</span>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Line Items</span>
                  <Badge variant="secondary">{lineItemRows.length}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Total Qty</span>
                  <span className="tabular-nums">{totals.totalQty.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-lg font-semibold pt-2 border-t">
                  <span>Total Cost</span>
                  <span className="tabular-nums">
                    {totals.totalCost === null
                      ? "-"
                      : (
                        <>
                          {formatCurrency(totals.totalCost, currency)}{" "}
                          {/* BLU-388: explicit ISO label so the line is
                              unambiguous on multi-currency POs. */}
                          <span className="font-mono uppercase text-xs text-muted-foreground">{currency}</span>
                        </>
                      )}
                  </span>
                </div>
                {isNonAud && totals.totalCost !== null && parsedRate > 0 ? (
                  <p className="text-sm text-muted-foreground text-right tabular-nums">
                    {/* BLU-388: append the org base currency code so "≈ $6.98"
                        isn't ambiguous — a tenant on a non-AUD org or a USD-PO
                        user shouldn't have to guess which $ this is. */}
                    {"≈"} {formatCurrency(Math.round((totals.totalCost * parsedRate + Number.EPSILON) * 100) / 100, orgBaseCurrency)}{" "}
                    <span className="font-mono uppercase text-[11px]">{orgBaseCurrency}</span>
                  </p>
                ) : null}
                {totals.missingCostCount > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Missing cost for {totals.missingCostCount} SKU{totals.missingCostCount === 1 ? "" : "s"}.
                  </p>
                ) : null}
                <p className="text-xs text-muted-foreground pt-2 border-t inline-flex items-center gap-1">
                  <ArrowUp className="h-3 w-3" aria-hidden="true" />
                  Save Draft / Submit live in the action bar above.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* STEP 2 — Line Items, gated until Order Details is valid.
              The lock notice is rendered as a SIBLING banner ABOVE the Card
              (not an absolute overlay) so it never collides with the
              CardHeader content visually. The Card itself dims, blocks
              pointer events, AND uses `inert` to remove it from the tab
              order — `pointer-events-none` alone leaves the form keyboard-
              focusable, which would let users edit a hidden state.
              On unlock, the Card runs unlock-glow once (DESIGN.md §4). */}
          {!orderDetailsValid && (
            <Alert className="border-amber-300 bg-amber-50 text-amber-900">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertTitle>Fill out the required Order Details first</AlertTitle>
              <AlertDescription className="mt-1 text-amber-800">
                {missingFields.length === 1
                  ? `Missing: ${missingFields[0]}.`
                  : `Missing: ${missingFields.join(", ")}.`}
              </AlertDescription>
            </Alert>
          )}
          <div
            // The wrapper carries the dim + select-none + the one-shot glow.
            // Card itself carries `inert` so keyboard tab skips its inputs.
            className={cn(
              "transition-opacity duration-300 motion-reduce:transition-none rounded-lg",
              !orderDetailsValid && "opacity-60 select-none",
              playUnlockGlow && "motion-safe:animate-unlock-glow",
            )}
          >
            <Card
              {...(!orderDetailsValid ? ({ inert: "" } as { inert: "" }) : {})}
              className={cn(!orderDetailsValid && "pointer-events-none")}
            >
            <CardHeader className="space-y-3">
              <div className="flex flex-row items-center justify-between gap-4">
                <CardTitle className="flex items-center gap-2">
                  Line Items
                  <Badge variant="secondary">
                    {lineItemRows.length} SKU{lineItemRows.length === 1 ? "" : "s"}
                  </Badge>
                </CardTitle>
                {lineItemRows.length > 0 && (
                  <div className="flex items-center gap-2">
                    {/* BLU-570: clear the whole order in one action (confirm-guarded). */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2.5 text-muted-foreground hover:text-orange-600"
                      onClick={() => setClearConfirmOpen(true)}
                    >
                      <Trash2 className="mr-1.5 h-4 w-4" />
                      Clear all
                    </Button>
                    <div className="flex items-center rounded-md border">
                      <Button
                        variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8 px-2.5 rounded-r-none"
                        onClick={() => setViewMode('list')}
                      >
                        <List className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8 px-2.5 rounded-l-none"
                        onClick={() => setViewMode('table')}
                      >
                        <LayoutGrid className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* New-location hint: all catalog SKUs are purchasable but have zero stock */}
              {products.length > 0 && products.every((p) => p.stockLevel === 0) && (
                <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  This location has no stock yet. All catalog SKUs are available to order; stock will be added when the PO is received.
                </div>
              )}

              {/* SKU search + CSV import */}
              <div className="relative" ref={dropdownRef}>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={searchInputRef}
                      value={skuSearch}
                      onChange={(e) => {
                        setSkuSearch(e.target.value);
                        if (e.target.value.trim()) {
                          setSkuDropdownOpen(true);
                        }
                      }}
                      onFocus={() => {
                        if (skuSearch.trim()) setSkuDropdownOpen(true);
                      }}
                      placeholder="Search SKUs by name, type, color, size..."
                      className="pl-9"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCsvImportModal(true)}
                    className="shrink-0"
                  >
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                    Import CSV
                  </Button>
                </div>

                {/* Dropdown results */}
                {skuDropdownOpen && skuSearch.trim() && (
                  <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border bg-popover shadow-lg">
                    <div className="max-h-[320px] overflow-y-auto">
                      {availableResults.length === 0 && addedResults.length === 0 && (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                          No SKUs found.
                        </div>
                      )}

                      {availableResults.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                          onClick={() => toggleCheckedSku(product.id)}
                        >
                          <Checkbox
                            checked={checkedSkuIds.has(product.id)}
                            onCheckedChange={() => toggleCheckedSku(product.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="shrink-0"
                          />
                          <span className="font-mono text-xs font-medium shrink-0">{product.sku}</span>
                          <span className="truncate text-muted-foreground">
                            {[product.title, product.category.type, product.category.color, product.category.size]
                              .filter(Boolean)
                              .join(" \u00b7 ")}
                          </span>
                          {product.stockLevel === 0 ? (
                            <Badge variant="outline" className="ml-auto shrink-0 text-[10px] tabular-nums">
                              0 stock
                            </Badge>
                          ) : (
                            <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
                              {product.stockLevel} in stock
                            </span>
                          )}
                        </button>
                      ))}

                      {addedResults.length > 0 && (
                        <>
                          {availableResults.length > 0 && (
                            <div className="border-t" />
                          )}
                          {addedResults.map((product) => (
                            <div
                              key={product.id}
                              className="flex w-full items-center gap-3 px-3 py-2 text-sm opacity-50"
                            >
                              <Check className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span className="font-mono text-xs font-medium shrink-0">{product.sku}</span>
                              <span className="truncate text-muted-foreground">
                                {[product.title, product.category.type, product.category.color, product.category.size]
                                  .filter(Boolean)
                                  .join(" \u00b7 ")}
                              </span>
                              <Badge variant="secondary" className="ml-auto text-xs">
                                Added
                              </Badge>
                            </div>
                          ))}
                        </>
                      )}
                    </div>

                    {/* Footer with selection actions */}
                    {checkedSkuIds.size > 0 && (
                      <div className="flex items-center justify-between border-t px-3 py-2">
                        <span className="text-sm text-muted-foreground">
                          {checkedSkuIds.size} selected
                        </span>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setCheckedSkuIds(new Set())}
                          >
                            Clear
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={addCheckedSkus}
                          >
                            Add {checkedSkuIds.size} SKU{checkedSkuIds.size === 1 ? "" : "s"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {viewMode === 'list' ? (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[48px]"></TableHead>
                        <TableHead className="w-[240px]">SKU</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-[120px] text-right">Qty</TableHead>
                        <TableHead className="w-[100px] text-right">In Stock</TableHead>
                        <TableHead className="w-[140px] text-right">Unit Cost</TableHead>
                        <TableHead className="w-[160px] text-right">Line Total</TableHead>
                        <TableHead className="w-[72px] text-right"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineItemRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                            No line items yet. Search for SKUs above to add them.
                          </TableCell>
                        </TableRow>
                      ) : (
                        lineItemRows.map((row) => (
                          <TableRow key={row.product.id}>
                            <TableCell>
                              <ProductImageWithHover
                                imageUrl={row.product.image_url}
                                productSku={row.product.sku}
                                size="medium"
                              />
                            </TableCell>
                            <TableCell className="font-medium">{row.product.sku}</TableCell>
                            <TableCell>{getProductDescription(row.product)}</TableCell>
                            <TableCell className="text-right">
                              <Input
                                value={row.lineItem.quantity}
                                onChange={(event) =>
                                  updateLineItemQuantity(row.product.id, event.target.value)
                                }
                                className="ml-auto w-24 text-right"
                                inputMode="numeric"
                                pattern="[0-9]*"
                              />
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {csvExtraProductMap.has(row.product.id) ? (
                                <Badge variant="secondary" className="font-medium">New</Badge>
                              ) : (
                                row.product.stockLevel
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="inline-flex items-center justify-end gap-1.5">
                                {/* BLU-388: indicator on the LEFT of the value
                                    so the rightmost glyph stays a digit and
                                    tabular alignment isn't broken across rows.
                                    WAC suppresses (already org base from the
                                    receive path); same-currency suppresses
                                    because the component returns null when
                                    sourceCurrency === displayCurrency. */}
                                {row.product.weightedAverageCost == null
                                  && row.product.categoryCostSourceAmount != null
                                  && row.product.categoryCostCurrency
                                  && row.product.categoryCostCurrency !== currency && (
                                  <ConvertedCostIndicator
                                    sourceAmount={row.product.categoryCostSourceAmount}
                                    sourceCurrency={row.product.categoryCostCurrency}
                                    displayCurrency={currency}
                                    rate={
                                      row.unitCost !== null && row.product.categoryCostSourceAmount > 0
                                        ? row.unitCost / row.product.categoryCostSourceAmount
                                        : null
                                    }
                                  />
                                )}
                                <span className="tabular-nums">
                                  {row.unitCost === null ? "-" : formatCurrency(row.unitCost, currency)}
                                </span>
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {row.lineTotal === null ? "-" : formatCurrency(row.lineTotal, currency)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeLineItem(row.product.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="space-y-6">
                  {lineItemRows.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                      No line items yet. Search for SKUs above to add them.
                    </div>
                  ) : (
                    Object.entries(matrixData).map(([type, data]) => (
                      <div key={type} className="rounded-lg border p-4">
                        <h3 className="text-base font-semibold mb-3">{type}</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr>
                                <th className="border border-border p-2 bg-muted text-left text-sm font-medium min-w-[160px]">
                                  Color
                                </th>
                                {data.sizes.map((size) => (
                                  <th key={size} className="border border-border p-2 bg-muted text-center text-sm font-medium min-w-[80px]">
                                    {size}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {data.colors.map((color) => {
                                const sampleImageUrl =
                                  Object.values(data.matrix[color] ?? {}).find((c) => c.imageUrl)?.imageUrl;
                                return (
                                  <tr key={color}>
                                    <td className="border border-border p-2">
                                      <div className="flex items-center gap-2">
                                        <ProductImageWithHover
                                          imageUrl={sampleImageUrl}
                                          productSku={`${type} - ${color}`}
                                          size="medium"
                                        />
                                        {renderColorCircle(color)}
                                        <span className="text-sm capitalize">{color}</span>
                                      </div>
                                    </td>
                                    {data.sizes.map((size) => {
                                      const cell = data.matrix[color]?.[size];
                                      if (!cell) {
                                        return (
                                          <td key={size} className="border border-border p-2 text-center text-muted-foreground">
                                            -
                                          </td>
                                        );
                                      }
                                      return (
                                        <td key={size} className="border border-border p-1 text-center">
                                          <Input
                                            value={cell.quantity}
                                            onChange={(e) => updateLineItemQuantity(cell.skuId, e.target.value)}
                                            className="w-16 mx-auto text-center h-8 text-sm"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                          />
                                          <div className="text-[10px] text-muted-foreground mt-0.5">
                                            {csvExtraProductMap.has(cell.skuId) ? 'New at location' : `${cell.stockLevel} in stock`}
                                          </div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
            </Card>
          </div>

        </main>

        <BackToTopPill />

        <QuickAddSupplierModal
          open={isSupplierModalOpen}
          onOpenChange={setIsSupplierModalOpen}
        />

        <PurchaseOrderCsvUploadModal
          products={products}
          isOpen={showCsvImportModal}
          onClose={() => setShowCsvImportModal(false)}
          onApply={handleCsvApply}
          poCurrency={currency}
          locationName={selectedLocation?.name ?? null}
        />

        {/* BLU-570: confirm before wiping every line item off the order. */}
        <AlertDialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Clear all products?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes all {lineItemRows.length} product{lineItemRows.length === 1 ? "" : "s"} from
                this order. This can't be undone. You'll need to add them again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setLineItems([]);
                  setClearConfirmOpen(false);
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Clear all
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Toaster />
      </div>
    </RoleGuard>
  );
};

export default function OrderCreateWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <OrderCreate />
    </PageErrorBoundary>
  );
}
